import React, { useState, useEffect } from "react";
import * as api from "../api";
import { C, R, F, numeric } from "../theme";
import { Icon } from "../icons";
import { Btn, Card, Spinner, Textarea, Callout, Confetti, Ref } from "./UI";

function ReviewPage({ orderId, setView }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!orderId) return;
    api.getOrder(orderId)
      .then(o => setOrder(o))
      .catch(() => setError("Commande introuvable"))
      .finally(() => setLoading(false));
  }, [orderId]);

  async function handleSubmit() {
    if (!rating) { setError("Veuillez sélectionner une note"); return; }
    setSubmitting(true);
    setError("");
    try {
      await api.submitReview({ order_id: orderId, phone: order.phone, rating, comment });
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  const renderStars = (val, interactive) => (
    <div
      style={{ display: "flex", gap: 6, justifyContent: "center" }}
      role={interactive ? "radiogroup" : undefined}
      aria-label={interactive ? "Note du service" : undefined}
    >
      {[1, 2, 3, 4, 5].map(i => {
        const active = i <= (interactive ? (hoverRating || rating) : val);
        const size = interactive ? 32 : 20;
        const content = (
          <Icon
            name="star"
            size={size}
            color={active ? C.amber : C.inkFaint}
            style={{ fill: active ? C.amber : "none" }}
          />
        );
        if (!interactive) return <span key={i}>{content}</span>;
        return (
          <button
            key={i}
            type="button"
            role="radio"
            aria-checked={rating === i}
            aria-label={`${i} étoile${i > 1 ? "s" : ""}`}
            onClick={() => setRating(i)}
            onMouseEnter={() => setHoverRating(i)}
            onMouseLeave={() => setHoverRating(0)}
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              padding: 4, borderRadius: R.sm, background: "transparent",
              border: "1px solid transparent", cursor: "pointer",
              transition: "transform .15s ease",
              transform: i <= (hoverRating || rating) ? "scale(1.06)" : "scale(1)",
            }}
          >
            {content}
          </button>
        );
      })}
    </div>
  );

  if (loading) return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Spinner />
    </div>
  );

  if (submitted) return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <Card className="animate-fadeInUp" style={{ maxWidth: 420, width: "100%", padding: 28, textAlign: "center" }}>
        <Confetti />
        <h2 style={{ fontSize: F.size.xl, fontWeight: F.weight.semi, color: C.ink, marginBottom: 6 }}>
          Merci pour votre avis
        </h2>
        <p style={{ fontSize: F.size.base, color: C.inkMuted, lineHeight: 1.55, marginBottom: 14 }}>
          Votre retour nous aide à améliorer nos services.
        </p>
        {renderStars(rating, false)}
        <div style={{ marginTop: 20 }}>
          <Btn icon="home" onClick={() => { window.history.replaceState({}, "", window.location.pathname); setView("landing"); }}>
            Retour à l'accueil
          </Btn>
        </div>
      </Card>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <Card className="animate-fadeInUp" style={{ maxWidth: 460, width: "100%", padding: 24 }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <h2 style={{ fontSize: F.size.xl, fontWeight: F.weight.semi, color: C.ink, letterSpacing: "-.01em" }}>
            Donnez votre avis
          </h2>
          <p style={{ fontSize: F.size.base, color: C.inkMuted, marginTop: 3 }}>Setal Express</p>
        </div>

        {order && (
          <div style={{
            background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: R.md,
            padding: "11px 13px", marginBottom: 20,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
              <Ref>{order.id}</Ref>
              <span style={{ fontSize: F.size.sm, color: C.inkMuted, ...numeric }}>{order.date}</span>
            </div>
            <div style={{ fontSize: F.size.base, color: C.inkBody, marginTop: 3 }}>{order.client}</div>
          </div>
        )}

        <div style={{ marginBottom: 20, textAlign: "center" }}>
          <p style={{ fontSize: F.size.base, fontWeight: F.weight.medium, color: C.inkBody, marginBottom: 10 }}>
            Comment évaluez-vous notre service ?
          </p>
          {renderStars(0, true)}
          {rating > 0 && (
            <p style={{ fontSize: F.size.base, color: C.navy, fontWeight: F.weight.semi, marginTop: 8 }}>
              {["", "Très insatisfait", "Insatisfait", "Correct", "Satisfait", "Excellent"][rating]}
            </p>
          )}
        </div>

        <Textarea
          label="Un commentaire ?"
          hint="optionnel"
          value={comment}
          onChange={setComment}
          rows={3}
          placeholder="Partagez votre expérience…"
        />

        {error && <Callout tone="danger" style={{ marginBottom: 14 }}>{error}</Callout>}

        <Btn
          onClick={handleSubmit}
          disabled={submitting || !rating}
          loading={submitting}
          fullWidth
          icon="send"
        >
          {submitting ? "Envoi…" : "Envoyer mon avis"}
        </Btn>

        <div style={{ marginTop: 12 }}>
          <Btn
            variant="subtle" small fullWidth icon="back"
            onClick={() => { window.history.replaceState({}, "", window.location.pathname); setView("landing"); }}
          >
            Retour à l'accueil
          </Btn>
        </div>
      </Card>
    </div>
  );
}

export default ReviewPage;
